/**
 * Configuration Presets - Save/load named configuration snapshots to localStorage
 */

import type { ConfigStore } from '@/store/configStore';

/** Fields excluded from snapshots (transient UI data) */
const UI_ONLY_KEYS: (keyof ConfigStore)[] = [
  'selectedKey', 'activeLayer', 'refreshTrigger',
  'zonePreview', 'powerBudget',
  'liveVisualizationEnabled',
];

export interface ConfigSnapshot {
  [key: string]: unknown;
}

export interface Preset {
  name: string;
  date: string; // ISO 8601
  snapshot: ConfigSnapshot;
}

const STORAGE_KEY = 'stak-presets';
const MAX_PRESETS = 20;

function readPresets(): Preset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as Preset[];
  } catch {
    return [];
  }
}

function writePresets(presets: Preset[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
}

export function listPresets(): Preset[] {
  return readPresets();
}

export function getConfigSnapshot(state: ConfigStore): ConfigSnapshot {
  const snapshot: ConfigSnapshot = {};
  const uiSet = new Set<string>(UI_ONLY_KEYS);
  const stateRecord = state as unknown as Record<string, unknown>;
  for (const key of Object.keys(stateRecord)) {
    if (uiSet.has(key)) continue;
    if (typeof stateRecord[key] === 'function') continue;
    snapshot[key] = stateRecord[key];
  }
  return snapshot;
}

export function savePreset(name: string, snapshot: ConfigSnapshot): void {
  const presets = readPresets();
  if (presets.some((p) => p.name === name)) {
    throw new Error(`Preset "${name}" already exists`);
  }
  if (presets.length >= MAX_PRESETS) {
    throw new Error(`Maximum ${MAX_PRESETS} presets reached`);
  }
  presets.push({ name, date: new Date().toISOString(), snapshot });
  writePresets(presets);
}

export function loadPreset(name: string): ConfigSnapshot | null {
  const presets = readPresets();
  return presets.find((p) => p.name === name)?.snapshot ?? null;
}

export function deletePreset(name: string): void {
  const presets = readPresets();
  writePresets(presets.filter((p) => p.name !== name));
}

export function renamePreset(oldName: string, newName: string): void {
  const presets = readPresets();
  if (presets.some((p) => p.name === newName)) {
    throw new Error(`Preset "${newName}" already exists`);
  }
  const preset = presets.find((p) => p.name === oldName);
  if (!preset) throw new Error(`Preset "${oldName}" not found`);
  preset.name = newName;
  writePresets(presets);
}
